import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Typography
} from '@material-ui/core';
import { useRegions } from '../../hooks/useRegions';
import { useJobProfilesByRegion } from '../../hooks/useJobProfilesByRegion';
import { useSkillDimensionsByJobProfile } from '../../hooks/useSkillDimensionsByJobProfile';

const UcIndicatorDialog = ({ open, onClose, onConfirm }) => {
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedQualification, setSelectedQualification] = useState('');
    const [selectedUoc, setSelectedUoc] = useState('');
    const [selectedIndicator, setSelectedIndicator] = useState('');

    const { data: regionsData, isFetching: isLoadingRegions, error: regionsError } = useRegions(open);
    const { data: jobProfilesData, isFetching: isLoadingJobProfiles, error: jobProfilesError } = useJobProfilesByRegion(selectedRegion);
    const { data: skillDimensionsData, isFetching: isLoadingSkillDimensions, error: skillDimensionsError } = useSkillDimensionsByJobProfile(selectedQualification);

    const regions = regionsData?.regions || [];
    const jobProfiles = jobProfilesData?.jobProfiles || [];
    const skillDimensions = skillDimensionsData?.skillDimensions || [];

    const selectedUocData = skillDimensions.find((sd) => sd.key === selectedUoc);
    const indicators = selectedUocData?.children || [];

    const handleClose = () => {
        setSelectedRegion('');
        setSelectedQualification('');
        setSelectedUoc('');
        setSelectedIndicator('');
        onClose();
    };

    const handleConfirm = () => {
        const indicatorData = indicators.find((i) => i.key === selectedIndicator);
        onConfirm({
            parentDimensionId: selectedUoc,
            childId: selectedIndicator,
            name: indicatorData?.name || ''
        });
        setSelectedRegion('');
        setSelectedQualification('');
        setSelectedUoc('');
        setSelectedIndicator('');
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ style: { padding: '16px' } }}>
            <DialogTitle>Select Unit of Competence Indicator</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{ marginTop: 4 }}>
                    {/* Row 1: Regions + Professional Qualifications */}
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Regions</InputLabel>
                            <Select
                                value={selectedRegion}
                                onChange={(e) => {
                                    setSelectedRegion(e.target.value);
                                    setSelectedQualification('');
                                    setSelectedUoc('');
                                    setSelectedIndicator('');
                                }}
                                label="Regions"
                                disabled={isLoadingRegions}
                                endAdornment={isLoadingRegions ? <CircularProgress size={20} style={{ marginRight: 24 }} /> : null}
                            >
                                <MenuItem value="">Select...</MenuItem>
                                {regions.map((region) => (
                                    <MenuItem key={region.key} value={region.key}>
                                        {region.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {regionsError && (
                            <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                                Error loading regions
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Professional Qualifications</InputLabel>
                            <Select
                                value={selectedQualification}
                                onChange={(e) => {
                                    setSelectedQualification(e.target.value);
                                    setSelectedUoc('');
                                    setSelectedIndicator('');
                                }}
                                label="Professional Qualifications"
                                disabled={!selectedRegion || isLoadingJobProfiles}
                                endAdornment={isLoadingJobProfiles ? <CircularProgress size={20} style={{ marginRight: 24 }} /> : null}
                            >
                                <MenuItem value="">Select...</MenuItem>
                                {jobProfiles.map((profile) => (
                                    <MenuItem key={profile.key} value={profile.key}>
                                        {profile.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {jobProfilesError && (
                            <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                                Error loading qualifications
                            </Typography>
                        )}
                    </Grid>

                    {/* Row 2: Units of Competence + Indicators */}
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Units of Competence</InputLabel>
                            <Select
                                value={selectedUoc}
                                onChange={(e) => {
                                    setSelectedUoc(e.target.value);
                                    setSelectedIndicator('');
                                }}
                                label="Units of Competence"
                                disabled={!selectedQualification || isLoadingSkillDimensions}
                                endAdornment={isLoadingSkillDimensions ? <CircularProgress size={20} style={{ marginRight: 24 }} /> : null}
                            >
                                <MenuItem value="">Select...</MenuItem>
                                {skillDimensions.map((sd) => (
                                    <MenuItem key={sd.key} value={sd.key}>
                                        {sd.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {skillDimensionsError && (
                            <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                                Error loading units of competence
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel>Indicators</InputLabel>
                            <Select
                                value={selectedIndicator}
                                onChange={(e) => setSelectedIndicator(e.target.value)}
                                label="Indicators"
                                disabled={!selectedUoc || indicators.length === 0}
                            >
                                <MenuItem value="">Select...</MenuItem>
                                {indicators.map((indicator) => (
                                    <MenuItem key={indicator.key} value={indicator.key}>
                                        {indicator.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="default">
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!selectedIndicator}
                    style={selectedIndicator ? { backgroundColor: '#ff9800', color: '#fff' } : undefined}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UcIndicatorDialog;
